export const indexPageContent = {
  'en-US': {
    hero: {
      title: 'Use quantitative strategies to capture the next trading opportunity',
      subtitle:
        'Our quantitative strategies monitor global markets in real time and surface entry or exit opportunities the moment they appear, notify you right away, and aid your decisions with confidence.'
    },
    performance: {
      title: 'Simulated Strategy Performance',
      description:
        'Simulated win rate, return, holding duration, and more surface at a glance. Select any model to review the full paper-trading history.',
      cta: 'View all strategies',
      loading: 'Refreshing simulated strategy performance...',
      errorPrefix: 'Failed to load simulated strategy performance: ',
      errorDefault: 'Unable to retrieve simulated strategy statistics',
      retry: 'Retry',
      regionLabel: '{region} market',
      metrics: {
        winRate: 'Win rate',
        avgReturnPerTrade: 'Average return / trade',
        totalReturn: 'Total return',
        tradeCount: 'Simulated trades analysed'
      },
      viewRecordCta: 'View complete simulated trade log →',
      empty: 'No simulated strategies are available right now. Please try again later.',
      previewLabel: 'Preview',
      previewTooltip: 'This strategy is a preview simulation model that is still being trained.'
    },
    capabilities: {
      title: 'Platform capabilities at a glance',
      description:
        'Explore the sections below for strategy evaluation, live trading signals, position management, and simulation backtesting, all paired with confidence scores to help you interpret recommendations.',
      cards: [
        {
          id: 'performance',
          icon: '📊',
          title: 'Strategy Performance',
          description:
            'Market-specific leaderboards highlight top-performing simulation models. Tap any model to inspect every simulated trade generated by the platform.',
          highlights: [
            'Simulated win rate, return, drawdown, and other core metrics at a glance',
            'One click opens the detailed simulated trade log for review',
            'Filter by market and strategy type to find the best-fit simulation model'
          ],
          cta: 'Open strategy performance',
          route: '/strategy/performance'
        },
        {
          id: 'entry-monitor',
          icon: '🚀',
          title: 'Entry Monitor',
          description:
            'Track recommended entry prices, target ranges, and model confidence for symbols across different markets.',
          highlights: [
            'Confidence score: model certainty scaled from 0–1',
            'Suggested entry range with estimated upside',
            'Filter quickly by market or your custom watchlists'
          ],
          cta: 'See entry opportunities',
          route: '/trading/entry'
        },
        {
          id: 'exit-monitor',
          icon: '🎯',
          title: 'Exit Monitor',
          description:
            'Import your current positions to receive suggested exit prices, stop-loss levels, and confidence scores to lock in profit or reduce drawdown.',
          highlights: [
            'Support for manual or batch position imports',
            'Provides take-profit/stop-loss targets with confidence hints',
            'Keeps a log of model suggestions for future review'
          ],
          cta: 'Manage exit plans',
          route: '/trading/exit'
        },
        {
          id: 'collections',
          icon: '⭐',
          title: 'Watchlists',
          description:
            'Pre-configure the securities and markets you care about so the platform prioritises related strategy signals.',
          highlights: [
            'Organise stocks and other assets into custom groups',
            'Watchlist symbols receive signal updates first',
            'Use watchlists directly when filtering entry/exit signals'
          ],
          cta: 'Configure watchlists',
          route: '/collections'
        },
        {
          id: 'simulation',
          icon: '🧪',
          title: 'Simulation History',
          description:
            'Run backtests with our quantitative strategies to evaluate performance across time ranges and scenarios.',
          highlights: [
            'Customise strategy, cost assumptions, and date ranges',
            'Generate return curves, trade lists, and risk indicators',
            'Provide evidence for team discussions and strategy tuning'
          ],
          cta: 'Start a simulation',
          route: '/simulation/history'
        }
      ],
      note:
        'About the “confidence” score: the model’s conviction is scaled from 0 to 1. The closer the value is to 1, the more consistent the model’s historical performance, which you can use as a quick gauge of signal reliability.'
    },
    riskNotice: {
      title: 'Risk disclaimer',
      messages: [
        'We do not provide live trading services or discretionary asset management.',
        'We only supply indicator data derived entirely from simulated trading.',
        'Investing involves risk; trade carefully.'
      ],
      confirm: 'I understand'
    }
  },
  'zh-CN': {
    hero: {
      title: '用量化策略捕捉下一次交易机会',
      subtitle:
        '我们的量化策略持续监控全球市场，当开仓或平仓的机会出现时，\n第一时间通知您，辅助您决策。'
    },
    performance: {
      title: '策略性能',
      description:
        '模拟盘胜率、收益、持仓周期等维度完整呈现。\n点击任意模型即可进入详细模拟交易记录。',
      cta: '查看全部策略',
      loading: '正在刷新策略表现...',
      errorPrefix: '加载策略性能数据失败：',
      errorDefault: '无法获取策略性能数据',
      retry: '重试',
      regionLabel: '{region} 市场',
      metrics: {
        winRate: '胜率',
        avgReturnPerTrade: '平均收益 / 交易',
        totalReturn: '累计收益',
        tradeCount: '样本交易数'
      },
      viewRecordCta: '查看完整交易记录 →',
      empty: '暂无可展示的策略，请稍后再试。',
      previewLabel: '预览版',
      previewTooltip: '该模型为预览版，仍在训练中'
    },
    capabilities: {
      title: '平台功能全览',
      description:
        '以下功能页覆盖策略评估、收盘后交易信号，配合“置信度”指标帮助你判断信号可靠度。',
      cards: [
        {
          id: 'performance',
          icon: '📊',
          title: '策略性能页面',
          description:
            '按市场划分的模型战绩榜单，胜率最高的策略优先展示，点击可查看每笔模拟成交记录。',
          highlights: [
            '胜率、收益、等核心指标一览',
            '点击即看每笔成交的策略复盘',
            '按市场与策略类型筛选匹配模型'
          ],
          cta: '进入策略性能',
          route: '/strategy/performance'
        },
        {
          id: 'entry-monitor',
          icon: '🚀',
          title: '开仓监控页面',
          description:
            '列出各市场标的物的推荐开仓价格与模型置信度，同时估算潜在收益。',
          highlights: [
            '置信度：模型自信指数 (0–1)',
            '展示可开仓区间与预估收益',
            '按市场或自选标的快速筛选'
          ],
          cta: '查看开仓机会',
          route: '/trading/entry'
        },
        {
          id: 'exit-monitor',
          icon: '🎯',
          title: '平仓监控页面',
          description:
            '录入已建仓的头寸后，系统自动计算合适的平仓价格与置信度，帮助锁定收益或控制回撤。',
          highlights: [
            '手动或批量导入当前仓位',
            '给出止盈/止损价与置信度提示',
            '同步记录模型建议，便于复盘'
          ],
          cta: '管理平仓计划',
          route: '/trading/exit'
        },
        {
          id: 'collections',
          icon: '⭐',
          title: '我的收藏夹页面',
          description:
            '提前配置关注的证券或市场标的，平台会优先推送相关策略信号。',
          highlights: [
            '自定义分组收藏股票等',
            '首屏优先显示关注资产的最新信号',
            '收藏清单直接参与开/平仓筛选'
          ],
          cta: '配置关注标的',
          route: '/collections'
        },
        {
          id: 'simulation',
          icon: '🧪',
          title: '模拟历史页面',
          description:
            '使用我们的量化策略进行自定义回测，验证策略在不同时间区间和条件下的表现。',
          highlights: [
            '自定义策略、费用、区间等参数',
            '生成收益曲线、交易列表、风险指标',
            '为团队讨论与策略调优提供依据'
          ],
          cta: '开始回测模拟',
          route: '/simulation/history'
        }
      ],
      note:
        '关于“置信度”指标：我们将模型对价格预测的把握程度量化为 0–1 的分值，数值越接近 1，代表模型在该场景下的历史表现越稳定，可用于衡量信号可信度。'
    },
    riskNotice: {
      title: '风险提示',
      messages: [
        '我们不提供实盘交易服务，也不进行代客理财服务。',
        '我们只提供指标数据，这些数据均为模拟盘数据。',
        '投资有风险，交易需谨慎。'
      ],
      confirm: '我已了解'
    }
  }
};
